<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <!-- Header -->
    <TopNavbar />

    <!-- ContactsHeader -->
    <CentralHeader
      :title="'All Contacts'"
      :count="totalContacts"
      :routeName="'CreateContactPage'"
      @search="handleSearch"
    />

    <!-- Main Content -->
    <div class="flex-1 flex flex-col mb-14 overflow-y-auto">
      <div
        v-if="isLoading"
        class="text-center flex-1 flex justify-center items-center m-auto h-full"
      >
        Loading contacts...
      </div>
      <div
        v-else-if="errorMessage"
        class="text-center flex-1 flex justify-center items-center m-auto h-full text-red-500"
      >
        {{ errorMessage }}
      </div>
      <div
        v-else-if="contacts.length === 0"
        class="text-center flex-1 flex justify-center items-center m-auto h-full text-total-white-500"
      >
        <button
          @click="goToCreateContact"
          class="text-prime-golden flex items-center gap-3"
        >
          <img src="../../assets/images/add-icon.svg" class="w-6 h-6" />
          Click to add a contact!
        </button>
      </div>
      <div v-else class="pb-[120px]">
        <div v-for="contact in contacts" :key="contact.id">
          <!-- Clicking on a contact card will take the user to that contact's details page -->
          <ContactCard
            :avatar="contact.profile_image_url"
            :name="getContactName(contact)"
            :role="contact.role === 'Project Manager' ? 'Project Manager on ' + contact.projects[0]?.address_1 : contact.role"
            :phoneNumber="contact.phone"
            @optionsClicked="openDrawer(contact)"
            @click="viewContact(contact.id)"
          />
        </div>
      </div>
    </div>

    <!-- Pagination Controls (Fixed at the bottom) -->
    <div
      class="flex justify-center items-center py-4 bg-pitch-black-900 fixed bottom-0 w-full border-t border-gray-600"
    >
      <button
        @click="previousPage"
        :disabled="currentPage <= 1 || isLoading"
        class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-left"></i>
      </button>

      <span class="text-sm text-total-white mx-4">
        {{ currentPage }} / {{ totalPages }}
      </span>

      <button
        @click="nextPage"
        :disabled="currentPage === totalPages || isLoading"
        class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>

    <!-- Contact Drawer -->
    <ContactDrawer
      :isOpen="isCommunicationDrawerOpen"
      :contact="selectedContact"
      @close="closeDrawer"
      @refresh="fetchContacts"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useContactsStore } from "@/stores/contact";
import CentralHeader from "@/components/CentralHeader.vue";
import ContactCard from "@/components/ContactCard.vue";
import ContactDrawer from "@/components/ContactDrawer.vue";
import TopNavbar from "@/components/TopNavbar.vue";

// Initialize router and store
const router = useRouter();
const contactsStore = useContactsStore();

// Reactive variables
const isCommunicationDrawerOpen = ref(false); // Track if communication drawer is open
const selectedContact = ref(null); // Store the selected contact data
const isLoading = ref(false);
const errorMessage = ref("");

// Pagination Variables
const currentPage = ref(1);
const itemsPerPage = ref(10); // Change this value to modify items per page
const totalPages = ref(1);
const totalContacts = ref(0);
const searchQuery = ref("");
const isSearchMode = ref(false);

// Computed properties
const contacts = computed(() => contactsStore.contacts);

// Methods
const fetchContacts = async (page = 1) => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    await contactsStore.fetchAllContacts(page);
    currentPage.value = contactsStore.totalContacts === 0 ? 0 : contactsStore.currentPage;
    totalPages.value =
      contactsStore.totalContacts === 0
        ? 0
        : Math.ceil(contactsStore.totalContacts / itemsPerPage.value);
    totalContacts.value = contactsStore.totalContacts;
  } catch (error) {
    errorMessage.value = "Failed to load contacts.";
    console.error("Error fetching contacts:", error);
  } finally {
    isLoading.value = false;
  }
};

const goToCreateContact = () => {
  router.push({ name: "CreateContactPage" });
};

const handleSearch = async (query, showSearch) => {
  isLoading.value = true;
  errorMessage.value = "";
  searchQuery.value = query;
  try {
    isSearchMode.value = !!query;
    currentPage.value = 1; // Reset to first page on search
    if (!query && showSearch) {
      await fetchContacts(currentPage.value);
    } else {
      await contactsStore.searchContacts(query, currentPage.value);
      currentPage.value =
        contactsStore.totalContacts === 0 ? 0 : contactsStore.currentPage;
      totalPages.value =
        contactsStore.totalContacts === 0
          ? 0
          : Math.ceil(contactsStore.totalContacts / itemsPerPage.value);
      totalContacts.value = contactsStore.totalContacts;
    }
  } catch (error) {
    errorMessage.value = "Failed to search contacts.";
    console.error("Error searching contacts:", error);
  } finally {
    isLoading.value = false;
  }
};

const nextPage = async () => {
  if (currentPage.value < totalPages.value && !isLoading.value) {
    currentPage.value += 1;
    if (isSearchMode.value) {
      await handleSearch(searchQuery.value);
    } else {
      await fetchContacts(currentPage.value);
    }
  }
};

const previousPage = async () => {
  if (currentPage.value > 1 && !isLoading.value) {
    currentPage.value -= 1;
    if (isSearchMode.value) {
      await handleSearch(searchQuery.value);
    } else {
      await fetchContacts(currentPage.value);
    }
  }
};

const getContactName = (contact) => {
  let baseName = `${contact.first_name} ${contact.last_name}`;

  if (baseName == " " || baseName == null || baseName == "Unknown Unknown"){
    baseName = contact.phone; 
  }

  if (baseName == null){
    baseName = contact.email;
  }

  return baseName;
};

const openDrawer = (contact) => {
  selectedContact.value = contact; // Set selected contact
  isCommunicationDrawerOpen.value = true; // Open the drawer
};

const closeDrawer = () => {
  isCommunicationDrawerOpen.value = false; // Close the drawer
};

// Navigate to the Contact View page
const viewContact = (contactId) => {
  router.push({ name: "ContactViewPage", params: { id: contactId } });
};

// Fetch contacts on component mount
onMounted(() => {
  fetchContacts();
});
</script>

<style scoped>
/* Add any necessary styles here */
</style>
